import { useParams } from 'react-router-dom'
import { unitId } from '@/annotations'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Pager } from '@/components/Pager'
import { Strip } from '@/components/Strip'
import { GridTable } from '@/components/GridTable'
import { useCursorPager } from '@/data/useCursorPager'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { formatJobTime } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type LostCommit = components['schemas']['LostCommit']

/**
 * `v-lostcommits` — process/09-SCREEN-SPECS/v-lostcommits.md. **No control on this screen at
 * all** — its own Actions table is empty, and it reads exactly one operation. A flat, cursor-paged
 * list, derived on demand at zero platform calls (rule 1: it reads the alert's own recorded
 * commits, never a live repository). The short SHA renders monospace; the full `commit` travels
 * only as the key, never alone as a handle (rule 3).
 *
 * **Not built this pass**: the `kpi-row` and `settings-panel` the catalogue lists for this unit —
 * no operation this screen calls supplies a repository name, a ref or an old-tip SHA (that
 * composition lives in `Alert.statement`, rendered on the `v-alerts` card that links here, per that
 * screen's own rule 6), and no operation offers a restore-to-new-branch affordance from this
 * screen's own Actions table. Reported rather than invented.
 *
 * **`panel-header`/`tools`/`strip` added W168** — a real "Refresh" `icon-button` (`tools`, scoped
 * to this screen's own query) and a real `strip` stating rule 1 in the customer's own words. The
 * spec's own remaining `strip` instances (in `settings-panel-body`) are the same not-built gap as
 * `kpi`/`field` above, not a separate one.
 */
export function VLostcommits() {
  const { alertId } = useParams<{ alertId: string }>()
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const pager = useCursorPager()

  const commits = useQuery({
    queryKey: ['lost-commits', orgId, workspaceId, alertId, pager.cursor],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/alerts/{alert_id}/lost-commits', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, alert_id: alertId! }, query: { cursor: pager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const problem = commits.error instanceof ApiProblemError ? commits.error.problem : undefined
  const rows = (commits.data?.items ?? []) as unknown as readonly LostCommit[]

  return (
    <div data-scr={unitId('v-lostcommits')}>
      <h1 className="m-0 mb-3 text-section font-semibold">The orphaned commits</h1>
      <Strip>This reads the alert's own recorded commits — never a live repository, and never a browse of the snapshot.</Strip>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mt-6">
        <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
          <span className="flex-1">Commits</span>
          <div data-cmp="tools">
            <Note>Newest first</Note>
            <IconButton
              data-act={undefined}
              icon="i-refresh"
              label="Refresh"
              disabled={commits.isFetching}
              onClick={() => queryClient.invalidateQueries({ queryKey: ['lost-commits', orgId, workspaceId, alertId] })}
            />
          </div>
        </div>
        <GridTable
          columns={[
            { key: 'commit', header: 'Commit', render: (c) => <span className="font-mono">{c.short_commit ?? c.commit}</span> },
            { key: 'author', header: 'Author', render: (c) => c.author },
            { key: 'date', header: 'Date (UTC)', render: (c) => formatJobTime(c.authored_at) },
            { key: 'message', header: 'Message', render: (c) => c.summary },
          ]}
          rows={rows}
          getRowKey={(c) => c.commit}
          emptyMessage={commits.isLoading ? '' : 'No commits were orphaned by this rewrite.'}
        />
        <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
          <Note>The objects are all still here — nothing about capture or retention changed when the branch moved. A commit is listed with its subject line only; there is no per-commit browse at MVP.</Note>
          <Pager hasPrev={pager.hasPrev} hasNext={Boolean(commits.data?.next_cursor)} loading={commits.isFetching} onPrev={pager.goPrev} onNext={() => commits.data?.next_cursor && pager.goNext(commits.data.next_cursor)} />
        </div>
      </div>

      <div className="mt-6">
        <Strip>
          What rewrite detection can and cannot see. We compare each backup against the one before it, so a rewrite
          made and reverted inside a single interval is invisible, and commits force-pushed away before their first
          backup were never ours. The rewrite is inferred from ref state rather than from an audit event —
          Bitbucket's reflog is server-side and not exposed — so where no push actor is available this says so rather
          than guessing.
        </Strip>
      </div>
    </div>
  )
}
